import { css } from "@emotion/css";
import { Badge, Callout, InlineEmptyStateOnboarding, ThirdPartyLogo, Tab, Tabs, Tooltip, useShowSnackbar } from "@octopusdeploy/design-system-components";
import { space, themeTokens } from "@octopusdeploy/design-system-tokens";
import { useQuery } from "@octopusdeploy/octopus-react-client";
import type { ActionProperties, ChannelResource, EnvironmentV2Resource, GetTeamsLookupBffResponseTeam, GitRefResource, ProcessTemplateParameterResource, ProjectSummaryResource, PropertyValueResource, VariableSetResource, WorkerPoolResource, } from "@octopusdeploy/octopus-server-client";
import { isSensitiveValue } from "@octopusdeploy/octopus-server-client";
import { isEqual } from "lodash";
import type * as React from "react";
import { useEffect, useState } from "react";
import semver from "semver/preload";
import { useAnalyticProcessTemplateConsumerActionDispatch, useAnalyticsStepEditorDispatch } from "~/analytics/Analytics";
import { deleteActionAndRedirect, getDeleteProcessMenuItem, isFoundProcessTemplateDetails, isMatchingProcessTemplate, processScopedEditPermission } from "~/areas/projects/components/Process/Common/CommonProcessHelpers";
import { ErrorsForAction } from "~/areas/projects/components/Process/Common/ErrorsForAction";
import StepName from "~/areas/projects/components/Process/Common/StepName";
import { useProcessContext } from "~/areas/projects/components/Process/Contexts/ProcessContext";
import { useProcessErrorSelectors } from "~/areas/projects/components/Process/Contexts/ProcessErrors/ProcessErrorsContext";
import { useProcessQueryStringContext } from "~/areas/projects/components/Process/Contexts/ProcessQueryString/ProcessQueryStringContext";
import { useProcessTemplatesFromContext, useRefreshProcessTemplatesFromContext } from "~/areas/projects/components/Process/Contexts/ProcessTemplatesContextProvider";
import type { FailedPermissionCheck, ProcessStepActionState } from "~/areas/projects/components/Process/ProcessStepsLayoutTypes";
import { ProcessSubPageLayout } from "~/areas/projects/components/Process/ProcessSubPageLayout";
import { ConsumerProcessTemplateParametersTab } from "~/areas/projects/components/Process/ProcessTemplates/ConsumerProcessTemplateParametersTab";
import ConsumerProcessTemplateSettingsTab from "~/areas/projects/components/Process/ProcessTemplates/ConsumerProcessTemplateSettingsTab";
import { shouldRenderParameter } from "~/areas/projects/components/Process/ProcessTemplates/TemplateParameterHelpers";
import { getProcessTemplateSlugFromAction, getProcessTemplateVersionMaskFromAction } from "~/areas/projects/components/Process/ProcessTemplates/processTemplateId";
import type { StoredAction, StoredStep } from "~/areas/projects/components/Process/types";
import { isDeploymentOrRunbookProcessIdentifier, isProjectTemplateProcessIdentifier, isTemplatedProjectProcessIdentifier, getProcessTemplateConsumerResourceType } from "~/areas/projects/components/Process/types";
import { getVersionMask, mapMaskToUpdateBehaviour, mapMaskToUsageConstraint, UpdateBehaviour, UsageConstraint } from "~/areas/projects/components/Templates/TemplateVersioningSection";
import type { LoadedLibraryVariableSets } from "~/areas/projects/components/Variables/AllVariables/SharedAllVariablesPage";
import getActionLogoUrl from "~/areas/projects/components/getActionLogoUrl";
import ActionList from "~/components/ActionList/index";
import type { DoBusyTask } from "~/components/DataBaseComponent/index";
import { ExpandableContainer } from "~/components/Expandable/index";
import { useIsLicensedFeatureEnabled } from "~/components/LicensedFeature/useIsLicensedFeatureEnabled";
import type { MenuItem } from "~/components/OverflowMenu/OverflowMenu";
import { OverflowMenu, OverflowMenuItems } from "~/components/OverflowMenu/OverflowMenu";
import convertPropertyValueResourceToString from "~/components/convertPropertyValueResourceToString";
import { UnstructuredFormSection } from "~/components/form/index";
import { resolveUrl } from "~/utils/resolveUrl";
interface ProcessTemplateUsageDetailsProps {
    step: StoredStep;
    action: StoredAction;
    stepOther: ProcessStepActionState;
    isNew: boolean;
    cleanAction: StoredAction | null;
    doBusyTask: DoBusyTask;
    gitRefResource: GitRefResource | undefined;
    releaseCreationPackageStepId: string | undefined;
    processVariableSet: VariableSetResource | FailedPermissionCheck;
    libraryVariableSets: LoadedLibraryVariableSets[] | FailedPermissionCheck;
    environments: EnvironmentV2Resource[];
    availableTargetTags: string[];
    availableWorkerPools: WorkerPoolResource[];
    teams: GetTeamsLookupBffResponseTeam[];
    isTenanted: boolean;
    channels: ChannelResource[];
    projects: ProjectSummaryResource[];
    refreshVariables: () => Promise<void>;
}
export type ConsumerProcessTemplateEditorUndefinedTab = "";
export type ConsumerProcessTemplateEditorTabs = "parameters" | "settings" | ConsumerProcessTemplateEditorUndefinedTab;
export const isConsumerProcessTemplateEditorTab = (value: string): value is ConsumerProcessTemplateEditorTabs => {
    return value === "parameters" || value === "settings" || value === "";
};
export function ProcessTemplateUsageDetails({ step, action, stepOther, isNew, cleanAction, doBusyTask, gitRefResource, releaseCreationPackageStepId, processVariableSet, libraryVariableSets, environments, availableTargetTags, availableWorkerPools, isTenanted, teams, channels, projects, refreshVariables, }: ProcessTemplateUsageDetailsProps) {
    const processContext = useProcessContext();
    const processErrorSelectors = useProcessErrorSelectors();
    const processTemplateContext = useProcessTemplatesFromContext();
    const refreshProcessTemplates = useRefreshProcessTemplatesFromContext();
    const isFeatureEnabled = useIsLicensedFeatureEnabled("ProcessTemplates");
    const processIdentifier = processContext.state.processIdentifier;
    const isProjectTemplate = isProjectTemplateProcessIdentifier(processIdentifier);
    const isTemplatedProject = isTemplatedProjectProcessIdentifier(processIdentifier);
    if (!isDeploymentOrRunbookProcessIdentifier(processIdentifier) && !isProjectTemplate && !isTemplatedProject) {
        throw new Error("Process templates can only be used in deployment, runbook, or project template processes.");
    }
    const projectIdForAnalytics = isProjectTemplate ? undefined : processIdentifier.projectId;
    const analyticsStepEditorDispatch = useAnalyticsStepEditorDispatch(step.Id, projectIdForAnalytics);
    const processTemplateConsumerActionDispatch = useAnalyticProcessTemplateConsumerActionDispatch(projectIdForAnalytics, getProcessTemplateConsumerResourceType(processIdentifier));
    const processTemplateSlug = getProcessTemplateSlugFromAction(action);
    const processTemplateVersionMask = getProcessTemplateVersionMaskFromAction(action);
    const actionErrors = action ? processErrorSelectors.getActionErrors(action.Id, processContext.selectors) : [];
    const usageErrors = processErrorSelectors.getProcessTemplateUsageErrors(step.Id);
    const [initialActionProperties, setInitialActionProperties] = useState(action.Properties);
    const [initialCleanModel, setInitialCleanModel] = useState(processContext.state.cleanModel);
    if (!isEqual(initialCleanModel, processContext.state.cleanModel)) {
        setInitialCleanModel(processContext.state.cleanModel);
        setInitialActionProperties(action.Properties);
    }
    const processQueryStringContext = useProcessQueryStringContext();
    const [activeTab, setActiveTab] = useState<ConsumerProcessTemplateEditorTabs>(processQueryStringContext.state.queryFilter.activeTab ? processQueryStringContext.state.queryFilter.activeTab : "parameters");
    const selectedProcessTemplateDetails = processTemplateContext.find((pt) => isMatchingProcessTemplate(pt, processTemplateSlug, processTemplateVersionMask));
    const [selectedProcessTemplate, setSelectedProcessTemplate] = useState(isFoundProcessTemplateDetails(selectedProcessTemplateDetails) ? selectedProcessTemplateDetails.processTemplate : undefined);
    const [prevProcessTemplateDetails, setPrevProcessTemplateDetails] = useState(selectedProcessTemplateDetails);
    const [initialProcessTemplateVersion, setInitialProcessTemplateVersion] = useState<string | undefined | null>(selectedProcessTemplateDetails?.processTemplateVersion);
    if (selectedProcessTemplateDetails !== prevProcessTemplateDetails) {
        setPrevProcessTemplateDetails(selectedProcessTemplateDetails);
        if (selectedProcessTemplateDetails && isFoundProcessTemplateDetails(selectedProcessTemplateDetails)) {
            setSelectedProcessTemplate(selectedProcessTemplateDetails.processTemplate);
        }
    }
    const getFieldError = (value: string) => {
        return processErrorSelectors.getActionFieldError(action.Id, processContext.selectors, value);
    };
    const setActionMetaProperties: <K extends keyof StoredAction>(state: Pick<StoredAction, K> | StoredAction, callback?: () => void) => void = (state, callback) => {
        processContext.actions.setActionMetaProperties(action.Id, (prev) => ({ ...prev, ...state }));
        callback && callback();
    };
    const setName = (value: string) => {
        setActionMetaProperties({ Name: value });
    };
    const setSlug = (value: string) => {
        setActionMetaProperties({ Slug: value });
    };
    const setDisabled = (value: boolean) => {
        setActionMetaProperties({ IsDisabled: value });
    };
    const setVersionMaskProperty = (versionMask: string) => {
        const properties: ActionProperties = {
            "Octopus.Action.ProcessTemplate.Reference.VersionMask": versionMask,
        };
        processContext.actions.setActionProperties(action.Id, properties);
    };
    const setParameterValue = (parameterName: string, value: PropertyValueResource | undefined) => {
        if ((isSensitiveValue(value) && value.HasValue) || (!isSensitiveValue(value) && value)) {
            const properties: ActionProperties = { [parameterName]: value };
            processContext.actions.setActionProperties(action.Id, properties);
        }
        else {
            processContext.actions.removeActionProperties(action.Id, [parameterName]);
        }
    };
    const [selectedUsageConstraint, setSelectedUsageConstraint] = useState<UsageConstraint>(mapMaskToUsageConstraint(processTemplateVersionMask));
    const [selectedUpdateBehaviour, setSelectedUpdateBehaviour] = useState<UpdateBehaviour>(mapMaskToUpdateBehaviour(processTemplateVersionMask));
    const { result: latestProcessTemplateVersionSummary } = useQuery(async (repository) => {
        return isProjectTemplateProcessIdentifier(processIdentifier) ? await repository.ProcessTemplates.getVersionSummaryFromSystem(processTemplateSlug) : await repository.ProcessTemplates.getVersionSummary(processTemplateSlug);
    }, [processTemplateSlug, processIdentifier], "Get Latest Published Process Template Summary");
    const showSnackbar = useShowSnackbar();
    const latestStableVersion = latestProcessTemplateVersionSummary?.LatestStableVersion;
    const latestPreReleaseVersion = latestProcessTemplateVersionSummary?.LatestPreReleaseVersion;
    const selectedProcessTemplateVersion = selectedProcessTemplateDetails?.processTemplateVersion;
    const latestVersionToCompare = selectedUsageConstraint === UsageConstraint.PreRelease ? latestPreReleaseVersion : latestStableVersion;
    const isNewVersionAvailable = selectedProcessTemplateVersion && latestVersionToCompare ? semver.ltr(selectedProcessTemplateVersion, latestVersionToCompare) : false;
    const isAutoUpdateAvailable = isNewVersionAvailable && latestVersionToCompare && getVersionMask(latestVersionToCompare, selectedUpdateBehaviour) === processTemplateVersionMask;
    const isManualUpdateAvailable = isNewVersionAvailable && latestVersionToCompare && getVersionMask(latestVersionToCompare, selectedUpdateBehaviour) !== processTemplateVersionMask;
    useEffect(() => {
        if (isAutoUpdateAvailable) {
            (async () => await refreshProcessTemplates())();
        }
    }, [isAutoUpdateAvailable, refreshProcessTemplates]);
    const onUpdateToLatestVersion = () => {
        if (latestStableVersion) {
            if (selectedUsageConstraint === UsageConstraint.PreRelease) {
                setSelectedUsageConstraint(UsageConstraint.Stable);
                setSelectedUpdateBehaviour(UpdateBehaviour.Minor);
                const versionMask = getVersionMask(latestStableVersion, UpdateBehaviour.Minor);
                setVersionMaskProperty(versionMask);
            }
            else {
                const versionMask = getVersionMask(latestStableVersion, selectedUpdateBehaviour);
                setVersionMaskProperty(versionMask);
            }
            setInitialProcessTemplateVersion(latestStableVersion);
            showSnackbar("Process template updated to latest version.");
        }
    };
    const visibleParameters = selectedProcessTemplate?.Parameters.filter((p) => shouldRenderParameter(p, action.Properties[p.Name], availableWorkerPools, isTenanted, isProjectTemplate));
    const requiredParametersWithoutValueCount = getParametersMissingValueCount(visibleParameters, action.Properties);
    const onTabChange = (_: React.ChangeEvent<object>, newValue: string) => {
        if (isConsumerProcessTemplateEditorTab(newValue)) {
            setActiveTab(newValue);
            processQueryStringContext.actions.changeProcessTemplateEditorActiveTab(newValue);
        }
    };
    return (<>
            <ExpandableContainer containerKey={action.Id}>
                <ProcessTemplateActionLayout step={step} action={action} stepOther={stepOther} setDisabled={setDisabled} releaseCreationPackageStepId={releaseCreationPackageStepId} templateVersion={selectedProcessTemplateVersion}>
                    <>
                        <ActionDisabledWarning action={action}/>
                        <ProcessTemplateUsageErrorsCallout usageErrors={usageErrors}/>
                        <ErrorsForAction actionErrors={actionErrors}/>
                        <Tabs value={activeTab} onChange={onTabChange} variant="scrollable">
                            <Tab value="parameters" label={<div className={tabLabelStyles}>
                                        <div>Parameters</div>
                                        {requiredParametersWithoutValueCount > 0 && (<Tooltip content={"Required parameter values are missing"}>
                                                <Badge size="small" variant="neutral" count={requiredParametersWithoutValueCount}/>
                                            </Tooltip>)}
                                    </div>}/>
                            <Tab value="settings" label={isManualUpdateAvailable && isFeatureEnabled ? (<div className={tabLabelStyles}>
                                            <div>Settings</div>
                                            <Badge size="small" variant="neutral" label="Update available"/>
                                        </div>) : ("Settings")}/>
                        </Tabs>
                        <>
                            {activeTab === "parameters" &&
            (selectedProcessTemplate ? (<ConsumerProcessTemplateParametersTab stepId={step.Id} action={action} doBusyTask={doBusyTask} onParameterValueChanged={setParameterValue} processTemplate={selectedProcessTemplate} gitRefResource={gitRefResource} libraryVariableSets={libraryVariableSets} processVariableSet={processVariableSet} environments={environments} availableWorkerPools={availableWorkerPools} availableTargetTags={availableTargetTags} analyticsStepEditorDispatch={analyticsStepEditorDispatch} isTenanted={isTenanted} teams={teams} channels={channels} projects={projects} refreshVariables={refreshVariables}/>) : (<>
                                        <div className={borderStyles}/>
                                        <InlineEmptyStateOnboarding illustration="NoEntities" entityName="parameters" title="No parameters found" description="This template may not have any parameters, or you haven’t selected a template yet."/>
                                    </>))}
                            {activeTab === "settings" && (<ConsumerProcessTemplateSettingsTab selectedProcessTemplate={selectedProcessTemplate} isNew={isNew} action={action} getFieldError={getFieldError} setName={setName} cleanAction={cleanAction} setSlug={setSlug} initialActionProperties={initialActionProperties} initialProcessTemplateVersion={initialProcessTemplateVersion} selectedProcessTemplateDetails={selectedProcessTemplateDetails} setVersionMaskProperty={setVersionMaskProperty} latestProcessTemplateVersionSummary={latestProcessTemplateVersionSummary} selectedUpdateBehaviour={selectedUpdateBehaviour} setSelectedUpdateBehaviour={setSelectedUpdateBehaviour} selectedUsageConstraint={selectedUsageConstraint} setSelectedUsageConstraint={setSelectedUsageConstraint} processVariableSet={processVariableSet} libraryVariableSets={libraryVariableSets} processTemplateConsumerActionDispatch={processTemplateConsumerActionDispatch} onUpdateToLatestVersion={onUpdateToLatestVersion} currentVersion={selectedProcessTemplateVersion}/>)}
                        </>
                    </>
                </ProcessTemplateActionLayout>
            </ExpandableContainer>
        </>);
}
export const isAnyRequiredParameterMissingValue = (parameters: ProcessTemplateParameterResource[] | undefined, actionProperties: ActionProperties): boolean => {
    return parameters?.some((p) => !p.IsOptional && p.Values.length === 0 && convertPropertyValueResourceToString(actionProperties[p.Name]) === "") ?? false;
};
export const getParametersMissingValueCount = (parameters: ProcessTemplateParameterResource[] | undefined, actionProperties: ActionProperties): number => {
    return parameters?.filter((p) => !p.IsOptional && p.Values.length === 0 && convertPropertyValueResourceToString(actionProperties[p.Name]) === "").length ?? 0;
};
const tabLabelStyles = css({
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: space[8],
});
const borderStyles = css({
    marginTop: space[1],
    borderTop: `${space[1]} solid ${themeTokens.color.processStep.border.default}`,
});
interface ProcessTemplateActionLayoutProps {
    step: StoredStep;
    action: StoredAction;
    stepOther: ProcessStepActionState;
    setDisabled: (value: boolean) => void;
    children: JSX.Element;
    releaseCreationPackageStepId: string | undefined;
    templateVersion: string | null | undefined;
}
function ProcessTemplateActionLayout({ step, action, setDisabled, stepOther, releaseCreationPackageStepId, children, templateVersion }: ProcessTemplateActionLayoutProps) {
    const processContext = useProcessContext();
    const processIdentifier = processContext.state.processIdentifier;
    const { actions: contextActions, selectors: contextSelectors } = processContext;
    const { actions: queryStringActions } = useProcessQueryStringContext();
    const stepActionNumber = `${contextSelectors.getStepNumber(step.Id)}`;
    const title = <StepName name={action.Name} number={stepActionNumber} stepType={stepOther.actionTypeName}/>;
    const logo = <ThirdPartyLogo url={resolveUrl(getActionLogoUrl(action))}/>;
    const menuItems: MenuItem[] = [];
    if (step && step.Id) {
        const processEditPermission = {
            permission: processScopedEditPermission(processContext.selectors.getProcessType()),
            project: isDeploymentOrRunbookProcessIdentifier(processIdentifier) ? processIdentifier.projectId : undefined,
            wildcard: true,
        };
        const enableItem = OverflowMenuItems.item(action.IsDisabled ? "Enable" : "Disable", () => setDisabled(!action.IsDisabled), processEditPermission);
        const deleteItem = getDeleteProcessMenuItem("step", async () => deleteActionAndRedirect(step, action, true, contextActions, contextSelectors, queryStringActions), processEditPermission, releaseCreationPackageStepId, processIdentifier, step, action, processContext);
        menuItems.push(enableItem, deleteItem);
    }
    const controlActions = [<OverflowMenu menuItems={menuItems} accessibleName="More Step Actions"/>];
    const sectionControl = <ActionList actions={controlActions}/>;
    return (<ProcessSubPageLayout title={title} titleLogo={logo} titleBadge={templateVersion ? `v${templateVersion}` : undefined} sectionControl={sectionControl}>
            {children}
        </ProcessSubPageLayout>);
}
interface ActionDisabledWarningProps {
    action: StoredAction;
}
function ActionDisabledWarning({ action }: ActionDisabledWarningProps) {
    if (!action.IsDisabled) {
        return null;
    }
    return (<UnstructuredFormSection stretchContent={true}>
            <Callout type={"warning"} title={"This step is currently disabled"}/>
        </UnstructuredFormSection>);
}
interface ProcessTemplateUsageErrorsCalloutProps {
    usageErrors: string[];
}
function ProcessTemplateUsageErrorsCallout({ usageErrors }: ProcessTemplateUsageErrorsCalloutProps) {
    if (usageErrors.length === 0) {
        return null;
    }
    return (<UnstructuredFormSection stretchContent={true}>
            <Callout type={"danger"} title={"Process template validation issues"}>
                <ul>
                    {usageErrors.map((error, index) => (<li key={index}>{error}</li>))}
                </ul>
            </Callout>
        </UnstructuredFormSection>);
}
